import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { ArrowLeft, Sparkles } from "lucide-react";
import { AttachmentPicker } from "@/components/AttachmentPicker";
import { SiteSelect } from "@/components/SiteSelect";
import { useAuth } from "@/contexts/AuthContext";
import { readDraft, writeDraft, clearDraft } from "@/lib/drafts";

const EMPTY_NEWS = { client_id: "", site_id: "", title: "", description: "", severity: "info" };

export default function VigiladorNewNews() {
  const nav = useNavigate();
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(EMPTY_NEWS);
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);
  // Fix BO (2026-11-03): borrador en el celular (ver lib/drafts.js).
  const { user } = useAuth();
  const draftKey = user ? `news:${user.id}` : null;
  const [draftReady, setDraftReady] = useState(false);
  const [restored, setRestored] = useState(false);
  useEffect(() => {
    if (!draftKey) return;
    const d = readDraft(draftKey);
    if (d) { setForm({ ...EMPTY_NEWS, ...(d.form || {}) }); setAttachments(d.attachments || []); setRestored(true); }
    setDraftReady(true);
  }, [draftKey]);
  useEffect(() => {
    if (!draftKey || !draftReady) return;
    if (form.title || form.description || attachments.length) writeDraft(draftKey, { form, attachments });
    else clearDraft(draftKey);
  }, [draftKey, draftReady, form, attachments]);
  const discardDraft = () => { clearDraft(draftKey); setForm(EMPTY_NEWS); setAttachments([]); setRestored(false); };

  useEffect(() => { (async () => {
    try { const { data } = await api.get("/clients"); setClients(data); }
    catch (e) { toast.error(formatApiError(e)); }
  })(); }, []);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/news", { ...form, attachments });
      clearDraft(draftKey);
      toast.success("Novedad cargada. Supervisor y administración la verán.");
      nav("/vigilador");
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ padding: "8px 16px" }}>
      <Link to="/vigilador" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--invict-text-muted)", fontSize: 12, marginBottom: 12, textDecoration: "none", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}><ArrowLeft size={14}/> Volver</Link>
      <h1 style={{ fontSize: 26, fontWeight: 900, margin: "4px 0 18px", color: "#0A0A0A", letterSpacing: "-0.02em" }}>Cargar novedad</h1>
      {restored && (
        <div className="card" style={{ padding: "10px 14px", marginBottom: 12, borderLeft: "4px solid #16a34a", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }} data-testid="news-draft-restored">
          <span style={{ fontSize: 12, fontWeight: 700 }}>Recuperamos lo que estabas cargando.</span>
          <button type="button" className="btn-ghost" onClick={discardDraft} style={{ padding: "6px 10px", fontSize: 11 }} data-testid="news-draft-discard">Descartar</button>
        </div>
      )}
      <form onSubmit={submit} className="card" style={{ padding: 18 }} data-testid="form-news-vig">
        <div className="field"><label>Cliente *</label>
          <select required value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value, site_id: "" })} data-testid="news-client">
            <option value="">Elegir cliente...</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <SiteSelect clientId={form.client_id} value={form.site_id} onChange={(v) => setForm({ ...form, site_id: v })} testIdPrefix="news"/>
        <div className="field"><label>Título * (máx 80)</label>
          <input maxLength={80} required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="ej. Movimiento extraño en perímetro norte" data-testid="news-title"/>
          <div style={{ fontSize: 10, color: "var(--invict-text-muted)", textAlign: "right" }}>{form.title.length}/80</div>
        </div>
        <div className="field"><label>Descripción *</label>
          <textarea required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Contá qué pasó con todo el detalle..." data-testid="news-desc"/>
        </div>
        <div className="field"><label>Prioridad</label>
          <div style={{ fontSize: 11, color: "var(--invict-text-muted)", background: "var(--invict-bone-2)", padding: "10px 12px", borderRadius: 4, display: "flex", alignItems: "center", gap: 8 }} data-testid="news-ai-severity-note">
            <Sparkles size={14} style={{ color: "#BF3437", flexShrink: 0 }}/>
            La prioridad (ALTA/MEDIA/BAJA) la clasifica un asistente de IA automáticamente al guardar. Un supervisor o admin puede corregirla después desde el listado de Novedades.
          </div>
        </div>
        <div className="field"><label>Fotos / Video</label>
          <AttachmentPicker value={attachments} onChange={setAttachments} max={4}/>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 700, margin: "4px 0 14px", cursor: "pointer" }}>
          <input type="checkbox" checked={form.visible_to_client !== false} onChange={(e) => setForm({ ...form, visible_to_client: e.target.checked })} data-testid="news-visible-client" />
          Visible para el cliente (portal de solo lectura)
        </label>
        <button type="submit" disabled={busy} className="btn-primary" style={{ width: "100%", padding: "14px" }} data-testid="news-submit">
          {busy ? "Guardando..." : "Guardar novedad"}
        </button>
      </form>
    </div>
  );
}
